import { AuthState, resolveAuthState } from './useAuthStateModel';
import { AuthContext } from '../types/auth';

/**
 * Pure model/presentation layer for the smart-login flow. Kept free of any
 * React / WebAuthn imports so the helpers are unit-testable (the WebAuthn
 * browser bundle is ESM-only and can't be parsed by the CRA Jest transform).
 * The hook in useSmartAuth.ts wires these to React state + ceremonies.
 */

/**
 * A message descriptor for the current auth state. `segments` are rendered in
 * order; a segment with `device: true` is where the view drops the <MyDevice/>
 * link. Keeping this as data (no JSX) keeps the model view-agnostic.
 */
export interface AuthMessage {
  severity: 'info' | 'error' | 'success';
  segments: { text?: string; device?: boolean }[];
}

export interface AuthActionButton {
  label: string;
  kind:
    | 'lookup' // not resolved yet — clicking (or blur/Enter) looks the user up
    | 'signIn'
    | 'addDevice'
    | 'requestAccess'
    | 'setupNewAccount'
    | 'checkAgain'
    | 'tryHybrid'
    | 'retry';
}

/** A single secondary text link beneath the primary button, if the state defines one. */
export interface SecondaryAction {
  label: string;
  kind: AuthActionButton['kind'];
}

export interface SmartAuthView {
  message: AuthMessage | null;
  /** The one primary action for this state (label + routing kind). */
  primary: AuthActionButton | null;
  /** At most one secondary text link; null when the state defines none. */
  secondary: SecondaryAction | null;
  /** Show the "try Sign in first — passkeys may sync" tip. */
  showSyncTip: boolean;
  /** Show the "Use my phone" hybrid-picker guidance note. */
  showHybridNote: boolean;
}

/** State the view binds the single primary button to. */
export interface SmartButtonState {
  label: string;
  kind: AuthActionButton['kind'] | null;
  disabled: boolean;
  busy: boolean;
  secondary?: SecondaryAction | null;
}

const seg = (text: string) => ({ text });
const deviceSeg = { device: true as const };

export const EMPTY_VIEW: SmartAuthView = {
  message: null,
  primary: null,
  secondary: null,
  showSyncTip: false,
  showHybridNote: false,
};

/** Lowercase + trim for all email comparisons. Pure. */
export const normalize = (s: string): string => s.toLowerCase().trim();

/**
 * Pure: the primary button is enabled only when a lookup has resolved for the
 * email currently in the field (not a stale one) and no lookup is in flight.
 */
export function isButtonEnabled(
  resolvedForEmail: string | null,
  email: string,
  busy: boolean,
): boolean {
  return (
    resolvedForEmail !== null && resolvedForEmail === normalize(email) && !busy
  );
}

/**
 * Pure: a lookup result is current only when its sequence number is the latest
 * AND the email it was for still matches what's in the field. Guards against
 * out-of-order responses and edits made while a lookup was in flight.
 */
export function isResultCurrent(
  requested: { requestedEmail: string; requestedSeq: number },
  current: { currentEmail: string; currentSeq: number },
): boolean {
  return (
    requested.requestedSeq === current.currentSeq &&
    requested.requestedEmail === normalize(current.currentEmail)
  );
}

/**
 * Pure: decide whether a `requestAccess` click should be SUPPRESSED (because an
 * identical request was just filed and the device row isn't visible yet) or
 * allowed to file. Extracted so the duplicate-request guard is unit-testable
 * without React. Suppress only when we already filed for this exact email AND
 * the current lookup surfaced no deviceRequest (the pre-visible race window).
 */
export function shouldSuppressRequest(input: {
  justRequestedEmail: string | null;
  email: string;
  hasDeviceRequest: boolean;
}): boolean {
  return (
    input.justRequestedEmail === normalize(input.email) &&
    !input.hasDeviceRequest
  );
}

/**
 * Pure multi-pass resolver for a context lookup. ReadyToSignIn-first ordering:
 * a usable local passkey always wins so a returning user is never dragged into
 * the add-device path by a stale device-request row. Calls resolveAuthState
 * ONLY — never a ceremony — so no OS prompt can fire during a lookup.
 */
export function resolveForLookup(ctx: AuthContext): {
  state: AuthState;
  intent: { intendsAddDevice?: boolean; intendsNewAccount?: boolean };
} {
  // (0) A usable local passkey always wins → plain sign-in. MUST precede the
  //     deviceRequest gate so a stale (registered/denied/expired) device row
  //     from this same IP never drags a returning user into add-device.
  if (ctx.userExists && ctx.hasCredentialForThisDevice) {
    return { state: resolveAuthState(ctx, {}), intent: {} };
  }
  // (1) A device request (meaningful only with NO local passkey) → surface its
  //     sub-state. Device wins over invite: it's the more specific "finish
  //     setup on THIS device" gesture.
  if (ctx.deviceRequest) {
    return {
      state: resolveAuthState(ctx, { intendsAddDevice: true }),
      intent: { intendsAddDevice: true },
    };
  }
  // (2) An invitation exists (brand-new user path), no local passkey.
  if (ctx.invite) {
    return {
      state: resolveAuthState(ctx, { intendsNewAccount: true }),
      intent: { intendsNewAccount: true },
    };
  }
  // (3) Plain context routing (unknown / known-device-unknown).
  return { state: resolveAuthState(ctx, {}), intent: {} };
}

export interface ViewOptions {
  intendsAddDevice?: boolean;
  intendsNewAccount?: boolean;
  inviteStatus?: string;
  /** The typed email, rendered in place of {user} in the locked copy. */
  email?: string;
}

/**
 * Pure: maps an AuthState (+ the intent that produced it) to exactly one
 * message + one primary action, plus at most one secondary link.
 */
export function viewForState(
  state: AuthState,
  opts: ViewOptions = {},
): SmartAuthView {
  const user = opts.email ?? '';

  switch (state) {
    case AuthState.ReadyToSignIn:
      if (opts.intendsAddDevice) {
        return {
          message: {
            severity: 'success',
            segments: [
              seg(`${user} is approved for `),
              deviceSeg,
              seg('. Finish setup now.'),
            ],
          },
          primary: { label: 'Add this device', kind: 'addDevice' },
          secondary: null,
          showSyncTip: false,
          showHybridNote: false,
        };
      }
      if (opts.intendsNewAccount) {
        return {
          message: {
            severity: 'success',
            segments: [
              seg("You're invited. Set up your account on "),
              deviceSeg,
              seg('.'),
            ],
          },
          primary: { label: 'Set up account', kind: 'setupNewAccount' },
          secondary: null,
          showSyncTip: false,
          showHybridNote: false,
        };
      }
      // Plain sign-in: no message, biometric fires on click.
      return {
        message: null,
        primary: { label: 'Sign in', kind: 'signIn' },
        secondary: null,
        showSyncTip: false,
        showHybridNote: false,
      };

    case AuthState.Unknown:
      return {
        message: {
          severity: 'info',
          segments: [seg(`${user} not recognized. Request access.`)],
        },
        primary: { label: 'Request access', kind: 'requestAccess' },
        secondary: null,
        showSyncTip: false,
        showHybridNote: false,
      };

    case AuthState.NewUserNeedsInvite:
      if (opts.inviteStatus === 'pending') {
        return {
          message: {
            severity: 'info',
            segments: [
              seg(
                "Your invitation is waiting for an admin to approve it. Check back once it's approved.",
              ),
            ],
          },
          primary: { label: 'Check again', kind: 'checkAgain' },
          secondary: null,
          showSyncTip: false,
          showHybridNote: false,
        };
      }
      // Expired / any non-active, non-pending invite.
      return {
        message: {
          severity: 'info',
          segments: [
            seg('Your invitation expired — ask an admin to send a new one.'),
          ],
        },
        primary: { label: 'Request access', kind: 'requestAccess' },
        secondary: null,
        showSyncTip: false,
        showHybridNote: false,
      };

    case AuthState.UserKnownDeviceUnknown:
      return {
        message: {
          severity: 'info',
          segments: [
            seg('We know your account, but not '),
            deviceSeg,
            seg('. Request access to use it here.'),
          ],
        },
        primary: { label: 'Request access', kind: 'requestAccess' },
        secondary: { label: 'Use my phone', kind: 'tryHybrid' },
        showSyncTip: true,
        showHybridNote: true,
      };

    case AuthState.DeviceRequestPending:
      return {
        message: {
          severity: 'info',
          segments: [
            seg(`${user} has a pending request — waiting for admin approval.`),
          ],
        },
        primary: { label: 'Check again', kind: 'checkAgain' },
        secondary: null,
        showSyncTip: false,
        showHybridNote: false,
      };

    case AuthState.ApprovalExpired:
      return {
        message: {
          severity: 'info',
          segments: [seg("Approval expired — you'll have to request again.")],
        },
        primary: { label: 'Request access', kind: 'requestAccess' },
        secondary: null,
        showSyncTip: false,
        showHybridNote: false,
      };

    case AuthState.DeviceNotApproved:
      return {
        message: {
          severity: 'info',
          segments: [
            seg('That request was closed. Request access for '),
            deviceSeg,
            seg(' again.'),
          ],
        },
        primary: { label: 'Request access', kind: 'requestAccess' },
        secondary: null,
        showSyncTip: false,
        showHybridNote: false,
      };

    case AuthState.PasskeyDismissedCanAddDevice:
      return {
        message: {
          severity: 'info',
          segments: [
            seg('Sign-in was cancelled. Try again, or add '),
            deviceSeg,
            seg(' if it has no passkey yet.'),
          ],
        },
        primary: { label: 'Try again', kind: 'retry' },
        secondary: { label: 'Add this device', kind: 'addDevice' },
        showSyncTip: true,
        showHybridNote: false,
      };

    case AuthState.PasskeyDismissed:
      return {
        message: {
          severity: 'info',
          segments: [
            seg('The sign-in prompt was dismissed or timed out. Try again.'),
          ],
        },
        primary: { label: 'Try again', kind: 'retry' },
        secondary: null,
        showSyncTip: false,
        showHybridNote: false,
      };

    case AuthState.PasskeyNoMatch:
      return {
        message: {
          severity: 'error',
          segments: [
            seg("That passkey didn't work on "),
            deviceSeg,
            seg('. You can re-add this device.'),
          ],
        },
        primary: { label: 'Add this device', kind: 'addDevice' },
        secondary: null,
        showSyncTip: true,
        showHybridNote: false,
      };

    default:
      return EMPTY_VIEW;
  }
}

/** The transient-failure view: a harmless, non-mutating re-check (never requestAccess). */
export const TRANSIENT_FAILURE_VIEW: SmartAuthView = {
  message: {
    severity: 'info',
    segments: [seg("Couldn't reach the server — try again.")],
  },
  primary: { label: 'Try again', kind: 'checkAgain' },
  secondary: null,
  showSyncTip: false,
  showHybridNote: false,
};
